<!--
  DistributorPicker -- SuperAdmin type-down search for ONE distributor.

  Nobody knows a distributor's numeric id, so SA tools that need "which
  distributor?" take a NAME fragment instead. The operator types >= 2
  characters, matches appear (debounced), and picking one emits the whole
  selected row: v-model is { id, name, plan, plan_id, plan_name, state,
  owner_email } or null. Callers use `id` (the number the write endpoints
  expect); `plan_id` / `plan_name` are the plan the subscription is pinned
  to in ANY state, so a trial reads "Plan: Test Plan (trial)" rather than
  "No active plan" (Bug Intake #416).

  Each option carries a disambiguator (owner email, current plan, id in
  muted text) so two similarly named distributors can be told apart.

  Out-of-order safety: every search aborts the previous request AND carries a
  sequence number, so a slow older response can never overwrite a newer one.

  Keyboard: ArrowDown/ArrowUp move, Enter picks the highlighted option (and
  never submits an enclosing form), Escape closes the list.

  Adopters so far: Plans & Pricing "Distributor plan assignment" and the SA
  Audit Log distributor filter.
-->
<template>
    <div class="dpicker" data-testid="sa-distributor-picker">
        <label v-if="label" class="dpicker__label" :for="inputId">
            {{ label }}
        </label>

        <div
            v-if="modelValue"
            class="dpicker__selected"
            data-testid="sa-distributor-picker-selected"
        >
            <div class="dpicker__selected-text">
                <strong>{{ modelValue.name }}</strong>
                <span class="dpicker__meta">
                    {{ planLabel(modelValue) }}
                    <span class="dpicker__id">#{{ modelValue.id }}</span>
                </span>
            </div>
            <button
                type="button"
                class="dpicker__clear"
                data-testid="sa-distributor-picker-clear"
                aria-label="Clear selected distributor"
                :disabled="disabled"
                @click="clear"
            >
                &times;
            </button>
        </div>

        <div v-else class="dpicker__box">
            <input
                :id="inputId"
                ref="input"
                v-model="query"
                class="dpicker__input"
                data-testid="sa-distributor-picker-input"
                type="text"
                role="combobox"
                autocomplete="off"
                :placeholder="placeholder"
                :disabled="disabled"
                aria-autocomplete="list"
                :aria-expanded="open ? 'true' : 'false'"
                :aria-controls="listboxId"
                :aria-activedescendant="activeOptionId"
                @input="onInput"
                @keydown="onKeydown"
                @focus="onFocus"
                @blur="onBlur"
            />

            <ul
                v-if="open"
                :id="listboxId"
                class="dpicker__list"
                data-testid="sa-distributor-picker-listbox"
                role="listbox"
            >
                <li
                    v-if="loading"
                    class="dpicker__status"
                    data-testid="sa-distributor-picker-loading"
                    role="presentation"
                >
                    Searching...
                </li>
                <li
                    v-else-if="error"
                    class="dpicker__status dpicker__status--error"
                    data-testid="sa-distributor-picker-error"
                    role="presentation"
                >
                    {{ error }}
                </li>
                <li
                    v-else-if="!results.length"
                    class="dpicker__status"
                    data-testid="sa-distributor-picker-empty"
                    role="presentation"
                >
                    No distributors match "{{ searchedFor }}".
                </li>
                <li
                    v-for="(d, i) in loading || error ? [] : results"
                    :id="optionId(d)"
                    :key="d.id"
                    class="dpicker__option"
                    :class="{ 'dpicker__option--active': i === activeIndex }"
                    :data-testid="`sa-distributor-picker-option-${d.id}`"
                    role="option"
                    :aria-selected="i === activeIndex ? 'true' : 'false'"
                    @mousedown.prevent
                    @mouseenter="activeIndex = i"
                    @click="select(d)"
                >
                    <span class="dpicker__name">{{ d.name }}</span>
                    <span class="dpicker__meta">
                        <span v-if="d.owner_email">{{ d.owner_email }} &middot; </span>
                        {{ planLabel(d) }}
                        <span class="dpicker__id">#{{ d.id }}</span>
                    </span>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import { searchDistributors } from "@/services/superadminDistributorService";

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;
let uid = 0;

export default {
    name: "DistributorPicker",
    props: {
        modelValue: { type: Object, default: null },
        label: { type: String, default: "Distributor" },
        placeholder: {
            type: String,
            default: "Type part of a distributor name...",
        },
        disabled: { type: Boolean, default: false },
    },
    emits: ["update:modelValue"],
    data() {
        uid += 1;
        return {
            uid,
            query: "",
            searchedFor: "",
            results: [],
            loading: false,
            error: "",
            focused: false,
            dismissed: false,
            activeIndex: -1,
            seq: 0,
        };
    },
    computed: {
        inputId() {
            return `dpicker-input-${this.uid}`;
        },
        listboxId() {
            return `dpicker-list-${this.uid}`;
        },
        open() {
            return (
                this.focused &&
                !this.dismissed &&
                this.searchedFor.length >= MIN_CHARS
            );
        },
        activeOptionId() {
            const d = this.results[this.activeIndex];
            return this.open && d ? this.optionId(d) : undefined;
        },
    },
    beforeUnmount() {
        clearTimeout(this.timer);
        if (this.controller) this.controller.abort();
    },
    methods: {
        optionId(d) {
            return `dpicker-${this.uid}-opt-${d.id}`;
        },
        planLabel(d) {
            // The pinned plan by NAME, whatever the subscription state; the
            // state is spelled out unless it is the ordinary "active".
            const name = d.plan_name || d.plan;
            if (!name) return "No plan";
            const suffix =
                d.state && d.state !== "active" ? ` (${d.state})` : "";
            return `Plan: ${name}${suffix}`;
        },
        onFocus() {
            this.focused = true;
        },
        onBlur() {
            this.focused = false;
        },
        onInput() {
            // Typing implies focus (and keeps the list open in tests/IME
            // paths where no focus event fires).
            this.focused = true;
            this.dismissed = false;
            clearTimeout(this.timer);
            const q = this.query.trim();
            if (q.length < MIN_CHARS) {
                // Too short to search: drop in-flight work and the list.
                this.seq += 1;
                if (this.controller) this.controller.abort();
                this.searchedFor = "";
                this.results = [];
                this.loading = false;
                this.error = "";
                this.activeIndex = -1;
                return;
            }
            this.timer = setTimeout(() => this.runSearch(q), DEBOUNCE_MS);
        },
        async runSearch(q) {
            if (this.controller) this.controller.abort();
            const controller = new AbortController();
            this.controller = controller;
            this.seq += 1;
            const mine = this.seq;
            this.searchedFor = q;
            this.loading = true;
            this.error = "";
            try {
                const rows = await searchDistributors(q, {
                    signal: controller.signal,
                });
                if (mine !== this.seq) return; // superseded
                this.results = rows;
                this.activeIndex = -1;
            } catch (e) {
                if (mine !== this.seq) return; // superseded / aborted
                this.results = [];
                this.error = "Could not search distributors. Try again.";
            } finally {
                if (mine === this.seq) this.loading = false;
            }
        },
        onKeydown(e) {
            const n = this.results.length;
            if (e.key === "ArrowDown") {
                e.preventDefault();
                this.dismissed = false;
                if (n) this.activeIndex = (this.activeIndex + 1) % n;
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                if (n) this.activeIndex = (this.activeIndex - 1 + n) % n;
            } else if (e.key === "Enter") {
                // Never let Enter submit the enclosing form from this input.
                e.preventDefault();
                const d = this.results[this.activeIndex];
                if (this.open && d) this.select(d);
            } else if (e.key === "Escape") {
                this.dismissed = true;
            }
        },
        select(d) {
            this.$emit("update:modelValue", { ...d });
            this.query = "";
            this.searchedFor = "";
            this.results = [];
            this.activeIndex = -1;
        },
        clear() {
            this.$emit("update:modelValue", null);
            this.$nextTick(() => {
                if (this.$refs.input) this.$refs.input.focus();
            });
        },
    },
};
</script>

<style scoped>
.dpicker {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 280px;
    position: relative;
}
.dpicker__label {
    font-size: 12px;
    font-weight: 600;
    color: #4a5568;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.dpicker__box {
    position: relative;
}
.dpicker__input {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    padding: 6px 10px;
    font-size: 14px;
    color: #1a202c;
    background: #fff;
}
.dpicker__list {
    position: absolute;
    z-index: 20;
    left: 0;
    right: 0;
    top: 100%;
    margin: 2px 0 0;
    padding: 0;
    list-style: none;
    max-height: 280px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}
.dpicker__status {
    padding: 8px 10px;
    font-size: 13px;
    color: #4a5568;
}
.dpicker__status--error {
    color: #c53030;
}
.dpicker__option {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 7px 10px;
    cursor: pointer;
}
.dpicker__option--active {
    background: #ebf4ff;
}
.dpicker__name {
    font-size: 14px;
    color: #1a202c;
}
.dpicker__meta {
    font-size: 12px;
    color: #4a5568;
}
.dpicker__id {
    color: var(--tf-fg-4, #4a5568);
    margin-left: 4px;
}
.dpicker__selected {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    padding: 5px 10px;
    background: #f7fafc;
}
.dpicker__selected-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.dpicker__clear {
    border: none;
    background: transparent;
    font-size: 20px;
    line-height: 1;
    color: #4a5568;
    cursor: pointer;
    padding: 0 4px;
}
.dpicker__clear:hover {
    color: #1a202c;
}
</style>
